import { getCurrencyFromRegion } from "@Components/LeadFormModal/utils";
import { MONTHS as months } from "constants/index";
import {
  FILTERS,
  ROOM_TYPE_COMBINED,
  RoomTypeCombined,
  STAY_DURATION_COMMON,
  STAY_DURATION_US,
} from "../components/FilterSection/data/filtersList";
import { getPricingCurrency } from "../components/SearchListItem/helper";
import { FilterState } from "./formatFilterState";
import { readStoredSelection } from "../components/FilterSection/components/Filters/Locality/localitySelection";
import { formatMoveInMonth } from "@Pages/PropertyPage/data/helper";
import currencyData from "constants/currencyConstants";
import { formatAmenityName } from "../components/FilterSection/components/Filters/AmenitiesFilter/utils/formatAmenityName";

export type FilterNameType = (typeof FILTERS)[number]["name"] | "Filters";

export type TranslateFn = (key: string) => string;

export const translateFilterOptionLabel = (label: string, t?: TranslateFn): string =>
  t ? t(label) : label;

export const getBudgetLabel = (
  filterState: FilterState,
  region: any,
  currencyExchangeRates?: any,
) => {
  const regionCurrency = getPricingCurrency(getCurrencyFromRegion(region?.combined_name));
  const currencyObject = currencyData?.find((currObj) => currObj?.symbol === regionCurrency);

  const exchangeRate = currencyExchangeRates?.rates?.find((item: any) => {
    const target = item?.target_currency?.toLowerCase();
    return target === currencyObject?.standard_name?.toLowerCase();
  });
  const exchangeCurrencyObject = currencyData?.find(
    (currObj) => currObj?.standard_name === currencyExchangeRates?.baseCurrency,
  );

  const currency = currencyExchangeRates?.baseCurrency
    ? exchangeCurrencyObject?.symbol
    : regionCurrency;

  const [min, max] = String(filterState.budget)?.split(",");
  const label =
    min && max
      ? `${currency}${
          exchangeRate?.rate ? Math.ceil(Number(min) / exchangeRate.rate) : min
        } - ${currency}${exchangeRate?.rate ? Math.ceil(Number(max) / exchangeRate.rate) : max}`
      : "Incorrect Value";

  return label;
};

export const getMoveInMonthLabel = (filterState: FilterState) => {
  const formattedMoveInMonth = formatMoveInMonth(filterState.moveInMonth);
  const value = `01-${String(formattedMoveInMonth)}`;
  const date = new Date(value);
  const month = months[date.getMonth()] || null;
  const year = date.getFullYear() || null;

  const label = month && year ? `${month}, ${year}` : `Incorrect Value`;
  return label;
};

export const getStayDurationLabel = (filterState: FilterState, t?: TranslateFn) => {
  if (filterState.minLeaseDuration === "all_selected") {
    return "";
  }

  const stayDurationTypes = [...STAY_DURATION_COMMON, ...STAY_DURATION_US];
  const activeLeaseDuration = stayDurationTypes.find((duration) =>
    duration.value.includes(String(filterState.minLeaseDuration)),
  );

  return translateFilterOptionLabel(activeLeaseDuration?.name || "Incorrect Value", t);
};

export const getRoomTypeLabel = (filterState: FilterState, t?: TranslateFn) => {
  const roomTypesList = ROOM_TYPE_COMBINED.reduce(
    (acc: RoomTypeCombined["list"], curr) => [...acc, ...curr.list],
    [],
  );

  const roomTypeListFiltered = roomTypesList.filter((item) =>
    filterState.roomType?.includes(item.value),
  );

  const selectedRoomTypes = roomTypeListFiltered.map((item) =>
    translateFilterOptionLabel(item.name, t),
  );
  const label =
    selectedRoomTypes.length > 1
      ? `${selectedRoomTypes[0]} +${selectedRoomTypes.length - 1}`
      : selectedRoomTypes[0];

  return label;
};

export const getUniversityLabel = (filterState: FilterState) => {
  const canonicalName = filterState?.university as string;
  const canonicalNameWithoutNum = canonicalName?.slice(0, canonicalName.lastIndexOf("-"));

  const university = canonicalNameWithoutNum
    ?.replace(/-/g, " ")
    ?.replace(/(^\w{1})|(\s{1}\w{1})/g, (match) => match?.toUpperCase()); // Capitalize first character after space

  return university || "";
};

export const getLocalityLabel = (filterState: FilterState) => {
  const canonicalName = filterState?.locality as string;
  if (!canonicalName) return "";

  // When multi-select is active, the selection lives in sessionStorage (see
  // Locality.tsx). The badge shows "First +N" like Amenities. If no stored
  // selection exists, fall through to the single-select label.
  const stored = readStoredSelection();
  if (stored.length > 0) {
    return stored.length > 1 ? `${stored[0].label} +${stored.length - 1}` : stored[0].label;
  }

  const canonicalNameWithoutNum = canonicalName?.slice(0, canonicalName.lastIndexOf("-"));

  const locality = canonicalNameWithoutNum
    ?.replace(/-/g, " ")
    ?.replace(/(^\w{1})|(\s{1}\w{1})/g, (match) => match?.toUpperCase()); // Capitalize first character after space

  return locality || "";
};

export const getHousingTypeLabel = (filterState: FilterState, t?: TranslateFn) => {
  const housingType = filterState?.housingType as string;

  const labels: Record<string, string> = {
    all: "All Accommodations",
    university_provider: "University Housing",
    private: "Private Housing",
  };

  return translateFilterOptionLabel(labels[housingType] || labels.all, t);
};

export const getAmenitiesLabel = (filterState: FilterState, t?: TranslateFn) => {
  const amenities = filterState?.amenities as string[];
  if (!amenities || amenities.length === 0) return "";

  const firstAmenity = translateFilterOptionLabel(formatAmenityName(amenities[0]), t);
  return amenities.length > 1 ? `${firstAmenity} +${amenities.length - 1}` : firstAmenity;
};

export const getBillsIncludedLabel = (filterState: FilterState, t?: TranslateFn) => {
  const billsIncluded = filterState?.billsIncluded as string[];
  if (!billsIncluded || billsIncluded.length === 0) return "";

  const firstName = translateFilterOptionLabel(formatAmenityName(billsIncluded[0]), t);
  return billsIncluded.length > 1 ? `${firstName} +${billsIncluded.length - 1}` : firstName;
};

const getFilterLabel = (
  filterName: FilterNameType,
  filterState: FilterState,
  region?: any,
  currencyExchangeRates?: any,
  t?: TranslateFn,
) => {
  switch (filterName) {
    case "Budget":
      return getBudgetLabel(filterState, region, currencyExchangeRates);

    case "Move in Month":
      return getMoveInMonthLabel(filterState);

    case "Stay Duration":
      return getStayDurationLabel(filterState, t);

    case "Room Type":
      return getRoomTypeLabel(filterState, t);

    case "University":
      return getUniversityLabel(filterState);

    case "Locality":
      return getLocalityLabel(filterState);

    case "Housing Type":
      return getHousingTypeLabel(filterState, t);

    case "Amenities":
      return getAmenitiesLabel(filterState, t);

    case "Bills Included":
      return getBillsIncludedLabel(filterState, t);

    case "Housing Fest":
      return "";

    default:
      return "";
  }
};

export default getFilterLabel;
